/**
 * knowledge/search.ts — semantic retrieval over accumulated knowledge.
 *
 * Pure scoring: natural-language queries are tokenized, expanded through a
 * semantic alias table ("cheap" → budget/lowCost, "fast internet" → internet
 * speed facts, …), and matched against titles, tags, facts, and text. Returns
 * ranked KNOWLEDGE OBJECTS, never raw string hits.
 */

import type { KnowledgeRecord } from './models';

/** Semantic aliases: query concept → equivalent tags/terms in stored knowledge. */
export const SEMANTIC_ALIASES: Record<string, string[]> = {
  cheap: ['budget', 'affordable', 'low-cost', 'inexpensive'],
  affordable: ['budget', 'cheap', 'low-cost'],
  fast: ['speed', 'quick', 'high-speed'],
  internet: ['wifi', 'broadband', 'connectivity', 'mbps'],
  quiet: ['calm', 'peaceful', 'residential', 'low-noise'],
  walkable: ['walkability', 'pedestrian', 'car-free'],
  safe: ['safety', 'secure', 'low-crime'],
  family: ['family-friendly', 'kids', 'children'],
  surfing: ['surf', 'beach', 'waves'],
  beach: ['coastal', 'seaside', 'surf'],
  nightlife: ['bars', 'clubs', 'evening'],
  food: ['restaurants', 'dining', 'cuisine'],
  coworking: ['workspace', 'cowork', 'desk'],
  remote: ['nomad', 'work', 'digital-nomad'],
  apartment: ['housing', 'flat', 'rental'],
  city: ['destination', 'location'],
  visa: ['immigration', 'permit', 'residency'],
};

const STOP_WORDS = new Set([
  'a', 'an', 'and', 'the', 'for', 'with', 'in', 'of', 'to', 'best', 'good',
  'under', 'over', 'per', 'month', 'is', 'are', 'what', 'where',
]);

export function tokenize(query: string): string[] {
  return query
    .toLowerCase()
    .split(/[^a-z0-9$]+/)
    .filter((t) => t.length > 1 && !STOP_WORDS.has(t));
}

/** Expand tokens with their semantic aliases (deduped). */
export function expandTerms(tokens: string[]): string[] {
  const out = new Set<string>(tokens);
  for (const t of tokens) for (const alias of SEMANTIC_ALIASES[t] ?? []) out.add(alias);
  return [...out];
}

/** Extract a "$1000" style budget cap from the query, in USD, or null. */
export function extractBudgetUsd(query: string): number | null {
  const m = query.match(/\$\s?(\d{2,6})/);
  return m ? Number(m[1]) : null;
}

export interface RelevanceHit {
  relevance: number;
  matchedTerms: string[];
}

/**
 * Score one record against expanded query terms. Weights: tag match > title
 * match > fact key/value match > free-text match. Budget caps filter on
 * monthlyCostUsd-style facts.
 */
export function scoreRecord(
  record: KnowledgeRecord,
  terms: string[],
  budgetUsd: number | null,
): RelevanceHit | null {
  if (terms.length === 0) return null;
  const title = record.title.toLowerCase();
  const text = (record.text ?? '').toLowerCase();
  const tags = record.tags.map((t) => t.toLowerCase());
  const factBlob = Object.entries(record.facts)
    .map(([k, v]) => `${k} ${String(v)}`)
    .join(' ')
    .toLowerCase();

  let score = 0;
  const matched = new Set<string>();
  for (const term of terms) {
    if (tags.some((t) => t.includes(term))) { score += 10; matched.add(term); continue; }
    if (title.includes(term)) { score += 7; matched.add(term); continue; }
    if (factBlob.includes(term)) { score += 5; matched.add(term); continue; }
    if (text.includes(term)) { score += 3; matched.add(term); }
  }
  if (matched.size === 0) return null;

  if (budgetUsd != null) {
    const cost = record.facts.monthlyCostUsd ?? record.facts.costOfLivingUsd;
    if (typeof cost === 'number') {
      if (cost > budgetUsd) return null; // over budget — excluded, not just demoted
      score += 8;
      matched.add(`under $${budgetUsd}`);
    }
  }

  // Coverage bonus: matching more distinct concepts beats one strong match.
  score += matched.size * 4;
  return {
    relevance: Math.min(100, Math.round(score)),
    matchedTerms: [...matched],
  };
}
